import { useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { motion, useReducedMotion } from "motion/react"
import type { TttCardItem, TttCardI18n } from "@/components/ttt/types"

interface TttProductCardProps {
  item: TttCardItem
  backpackSrc: string
  holiday: boolean
  i18n: TttCardI18n
}

interface Burst {
  id: number
  value: number
}

// Faithful React port of app/views/ttt/shared/_card.html.slim: hachures card,
// image that tilts on hover, floating name box that reveals the description,
// and a price pill that expands to "Ajouter au panier" with the backpack icon.
export default function ProductCard({
  item,
  backpackSrc,
  holiday,
  i18n,
}: TttProductCardProps) {
  const prefersReducedMotion = useReducedMotion()
  const [bursts, setBursts] = useState<Burst[]>([])
  const countRef = useRef(0)

  // Adds to cart and fires a "+N" starburst pop (React port of ttt--backet-popup).
  const addToCart = () => {
    countRef.current += 1
    const burst = { id: countRef.current, value: countRef.current }
    setBursts((prev) => [...prev, burst])
    router.post(item.add_to_cart_url, {}, { preserveScroll: true })
  }

  const removeBurst = (id: number) =>
    setBursts((prev) => prev.filter((b) => b.id !== id))

  return (
    <div className="h-full max-h-[30rem] mx-auto font-ttt-title">
      <div className="flex flex-col gap-3 justify-between relative max-w-96 py-1 bg-cover bg-auto rounded-2xl group hachures h-full hover:bg-none">
        <a
          href={item.url}
          className="h-full mx-3 my-2 overflow-hidden transition-all duration-700 ease-out origin-center rounded-xl group-hover:rotate-6 group-hover:-translate-y-5 relative"
        >
          {item.cover_image_url ? (
            <img
              src={item.cover_image_url}
              alt={item.name}
              className="h-full w-full object-cover object-center min-h-80"
            />
          ) : (
            <div className="h-full w-full min-h-80 bg-light flex items-center justify-center text-primary">
              {i18n.no_image}
            </div>
          )}
          {item.pre_sale && (
            <div className="absolute top-4 left-4">
              <span
                title={item.pre_sale_tooltip ?? undefined}
                className="bg-gray-200 text-content text-sm font-medium px-3 py-1 rounded"
              >
                {i18n.pre_sale}
              </span>
            </div>
          )}
        </a>

        <a
          href={item.url}
          className="absolute flex flex-col items-center bg-light rounded-xl text-center pt-2 border border-primary translate-y-3 bottom-12 -left-5 -right-5"
        >
          <h1 className="text-xl px-3 font-[1000] text-primary">
            {item.name.toUpperCase()}
          </h1>
          {item.sub_name && (
            <p className="text-lg px-3 block text-primary">{item.sub_name}</p>
          )}

          <span className="mt-2 overflow-hidden ease-out grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 group-hover:grid-rows-[1fr]">
            <span className="overflow-hidden px-3">
              <span className="block w-full border-t border-primary" />
              <span className="block py-2 text-primary italic">
                {`"${item.description.slice(0, 100)} ..."`}
              </span>
            </span>
          </span>
        </a>

        <div
          className={`w-fit absolute -bottom-8 left-1/2 -translate-x-1/2 relative bg-light rounded-xl text-center py-2 max-h-12 min-h-12 border border-primary flex items-center overflow-hidden transition-all duration-200 hover:overflow-visible group/link hover:max-w-full ${
            holiday ? "" : "max-w-28"
          }`}
        >
          <div className="uppercase cursor-default">
            {item.soldout ? (
              <div className="px-3 text-primary font-[1000] text-2xl whitespace-nowrap">
                {i18n.soldout}
              </div>
            ) : holiday ? (
              <div className="px-3 text-xs text-center text-primary">
                {i18n.holiday}
              </div>
            ) : (
              <button
                type="button"
                onClick={addToCart}
                className="flex items-center px-3 gap-x-2 relative group/button cursor-pointer"
              >
                <span className="text-primary font-[1000] text-2xl group-hover/link:w-auto whitespace-nowrap">
                  {item.price}
                </span>
                <span className="text-primary mr-3 transition-all opacity-0 whitespace-nowrap delay-200 group-hover/link:opacity-100">
                  Ajouter au panier
                </span>
                <span className="text-xl -right-6 absolute transition-all opacity-0 delay-200 group-hover/link:opacity-100">
                  <img src={backpackSrc} alt="" className="w-10 h-10" />
                </span>

                {bursts.map((burst) => (
                  <motion.span
                    key={burst.id}
                    className="absolute -top-5 -right-10 w-10 h-10 pointer-events-none z-10"
                    initial={{ opacity: 0, scale: 0.3, y: 6 }}
                    animate={
                      prefersReducedMotion
                        ? { opacity: [0, 1, 0], scale: 1, y: 0 }
                        : {
                            opacity: [0, 1, 1, 0],
                            scale: [0.3, 1.2, 1, 0.9],
                            y: [6, -6, -16, -28],
                          }
                    }
                    transition={{
                      duration: prefersReducedMotion ? 0.7 : 0.9,
                      ease: "easeOut",
                      times: prefersReducedMotion ? [0, 0.3, 1] : [0, 0.2, 0.6, 1],
                    }}
                    onAnimationComplete={() => removeBurst(burst.id)}
                  >
                    <span className="bg-content text-light flex items-center justify-center absolute right-0 top-0 starburst text-sm font-bold">
                      +{burst.value}
                    </span>
                  </motion.span>
                ))}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
